import { FaceLandmarker, FilesetResolver, type FaceLandmarkerResult } from '@mediapipe/tasks-vision';
import type { LipDetection, FacePose } from './faceLandmarker';

const BASE = import.meta.env.BASE_URL;
const WASM_BASE = `${BASE}assets/mediapipe-wasm`;
const MODEL_URL = `${BASE}assets/models/face_landmarker.task`;

const NEUTRAL_POSE: FacePose = { matrix: null, roll: 0, yaw: 0, pitch: 0 };

// Mirror of extractPose() in faceLandmarker.ts (kept local to avoid exporting
// internals). Column-major 4x4 -> canvas-friendly euler angles.
function extractPose(raw: { data: number[] } | undefined): FacePose {
  if (!raw || !raw.data || raw.data.length < 16) return NEUTRAL_POSE;
  const m = raw.data;
  const r01 = m[4]!, r11 = m[5]!, r12 = m[9]!, r02 = m[8]!, r22 = m[10]!, r20 = m[2]!, r00 = m[0]!;
  const roll = Math.atan2(r01, r11);
  const pitch = Math.asin(Math.max(-1, Math.min(1, -r12)));
  const yaw = Math.abs(r12) < 0.9999 ? Math.atan2(r02, r22) : Math.atan2(-r20, r00);
  return { matrix: m, roll, yaw, pitch };
}

export class LiveTracker {
  private instance: Promise<FaceLandmarker> | null = null;

  private create(): Promise<FaceLandmarker> {
    return FilesetResolver.forVisionTasks(WASM_BASE).then((fileset) =>
      FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODEL_URL },
        runningMode: 'VIDEO',
        numFaces: 1,
        minFaceDetectionConfidence: 0.6,
        minFacePresenceConfidence: 0.6,
        outputFaceBlendshapes: false,
        outputFacialTransformationMatrixes: true
      })
    );
  }

  async detect(video: HTMLVideoElement, timestampMs: number): Promise<LipDetection | null> {
    if (!this.instance) this.instance = this.create();
    const landmarker = await this.instance;
    const result: FaceLandmarkerResult = landmarker.detectForVideo(video, timestampMs);
    const face = result.faceLandmarks?.[0];
    if (!face || face.length < 478) return null;
    return {
      landmarks: face.map((p) => ({ x: p.x, y: p.y, z: p.z })),
      imageWidth: video.videoWidth,
      imageHeight: video.videoHeight,
      pose: extractPose(result.facialTransformationMatrixes?.[0])
    };
  }

  async dispose(): Promise<void> {
    if (!this.instance) return;
    const l = await this.instance.catch(() => null);
    l?.close();
    this.instance = null;
  }
}
